import { html, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import css from './lib-stepper.css';

/** Current position and next destination in a workflow. */
@customElement('lib-stepper')
export class LibStepper extends LibElement {
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];

  @property({ type: Number, attribute: 'step-count' }) stepCount = 3;
  @property({ type: Number, attribute: 'current-step' }) currentStep = 1;
  @property({ attribute: 'current-step-text' }) currentStepText: string | null = null;
  @property({ attribute: 'next-step-text' }) nextStepText: string | null = null;
  @property({ attribute: 'step-prefix' }) stepPrefix = 'Step';
  @property({ attribute: 'next-prefix' }) nextPrefix = 'Next:';
  @property({ attribute: 'finish-text' }) finishText = 'Finish';
  @property({ attribute: 'of-text' }) ofText = 'of';
  @property({ attribute: 'aria-label' }) override ariaLabel = 'Step progress';

  override render() {
    const count = Math.max(1, Math.trunc(this.stepCount) || 1);
    const current = Math.min(count, Math.max(1, Math.trunc(this.currentStep) || 1));
    const compose = (prefix: string, value: string | number) =>
      [prefix, String(value)].filter(text => text.trim().length > 0).join(' ');
    const destination = current === count ? this.finishText : compose(this.stepPrefix, current + 1);
    return html`<div class="lib-stepper" role="group" aria-label=${this.ariaLabel}>
      <div class="lib-stepper-indicator" style="--lib-stepper-progress: ${360 * current / count}deg;" aria-hidden="true">
        <span class="lib-stepper-step-count">${current} ${this.ofText} ${count}</span>
      </div>
      <div class="lib-stepper-information">
        <span class="lib-stepper-current">${this.currentStepText ?? compose(this.stepPrefix, current)}</span>
        <span class="lib-stepper-next">${this.nextStepText ?? compose(this.nextPrefix, destination)}</span>
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-stepper': LibStepper; }
}
